import React, { useEffect, useState } from "react";
import { useLang } from "../../context/LanguageContext";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { buildWaLink } from "../../lib/constants";
import { Menu, X } from "lucide-react";

export const Header = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: t.nav.about },
    { href: "#suite", label: t.nav.suite, highlight: true },
    { href: "#rooms", label: t.nav.rooms },
    { href: "#gallery", label: t.nav.gallery },
    { href: "#location", label: t.nav.location },
    { href: "#contact", label: t.nav.contact },
  ];

  const bg = scrolled
    ? "bg-[#FDFBF7]/85 backdrop-blur-xl border-b border-stone-200/60"
    : "bg-gradient-to-b from-black/40 to-transparent";

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${bg}`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 md:h-24 flex items-center justify-between">
        {/* Logo */}
        <a
          href="#top"
          data-testid="logo-link"
          className="group flex flex-col leading-none"
        >
          <span
            className={`font-heading text-xl md:text-[26px] leading-tight transition-colors ${
              scrolled ? "text-stone-900" : "text-white drop-shadow-lg"
            }`}
          >
            <span className="block md:inline">Annette Rooms</span>
            <span className="hidden md:inline text-amber-300/90"> · </span>
            <span className="block md:inline">Suite maXimo</span>
          </span>
          <span
            className={`text-[10px] uppercase tracking-[0.3em] mt-1 transition-colors ${
              scrolled ? "text-stone-500" : "text-white/85 drop-shadow"
            }`}
          >
            Bari Palese · Puglia
          </span>
        </a>

        {/* Desktop Nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {links.map((l) => {
            const baseColor = scrolled
              ? l.highlight
                ? "text-amber-800 italic font-medium"
                : "text-stone-700 hover:text-stone-900"
              : l.highlight
              ? "text-amber-200 italic font-medium drop-shadow-md"
              : "text-white/95 hover:text-white drop-shadow-md";
            return (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-${l.href.replace("#", "")}`}
                className={`text-sm transition-colors relative group ${baseColor}`}
              >
                {l.label}
                <span
                  className={`absolute -bottom-1 left-0 w-0 h-px transition-all duration-500 group-hover:w-full ${
                    scrolled ? "bg-stone-900" : "bg-white"
                  }`}
                />
              </a>
            );
          })}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3 md:gap-5">
          {/* Language switcher */}
          <div
            className="flex items-center text-xs uppercase tracking-[0.2em]"
            data-testid="language-switcher"
          >
            <button
              data-testid="lang-it"
              onClick={() => setLang("it")}
              className={`px-2 py-1 transition-colors ${
                scrolled
                  ? lang === "it"
                    ? "text-stone-900 font-semibold"
                    : "text-stone-400 hover:text-stone-700"
                  : lang === "it"
                  ? "text-white font-semibold drop-shadow"
                  : "text-white/60 hover:text-white drop-shadow"
              }`}
            >
              IT
            </button>
            <span className={scrolled ? "text-stone-300" : "text-white/40"}>/</span>
            <button
              data-testid="lang-en"
              onClick={() => setLang("en")}
              className={`px-2 py-1 transition-colors ${
                scrolled
                  ? lang === "en"
                    ? "text-stone-900 font-semibold"
                    : "text-stone-400 hover:text-stone-700"
                  : lang === "en"
                  ? "text-white font-semibold drop-shadow"
                  : "text-white/60 hover:text-white drop-shadow"
              }`}
            >
              EN
            </button>
          </div>

          <a
            href={buildWaLink(
              lang === "it"
                ? "Ciao Annette Rooms, vorrei informazioni sulla disponibilità."
                : "Hello Annette Rooms, I'd like information on availability."
            )}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-cta"
            className="hidden md:inline-flex items-center gap-2 bg-stone-900 text-white px-5 py-3 text-xs uppercase tracking-[0.2em] hover:bg-stone-800 transition-colors"
          >
            <WhatsAppIcon className="w-4 h-4" color="#25D366" />
            {t.nav.book}
          </a>

          {/* Mobile menu button */}
          <button
            className={`lg:hidden w-10 h-10 flex items-center justify-center transition-colors ${
              scrolled || open ? "text-stone-900" : "text-white drop-shadow-lg"
            }`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="w-7 h-7" strokeWidth={2} /> : <Menu className="w-7 h-7" strokeWidth={2} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-500 bg-[#FDFBF7] shadow-2xl ${
          open ? "max-h-[80vh] border-t border-stone-200" : "max-h-0"
        }`}
      >
        <nav className="px-6 py-8 flex flex-col gap-5">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`mobile-nav-${l.href.replace("#", "")}`}
              onClick={() => setOpen(false)}
              className={`font-heading text-3xl ${
                l.highlight ? "text-amber-800 italic" : "text-stone-900"
              }`}
            >
              {l.label}
            </a>
          ))}
          <a
            href={buildWaLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="mobile-whatsapp-cta"
            className="mt-4 inline-flex items-center justify-center gap-2 bg-[#25D366] text-white px-6 py-4 text-xs uppercase tracking-[0.2em]"
          >
            <WhatsAppIcon className="w-4 h-4" />
            {t.hero.cta}
          </a>
        </nav>
      </div>
    </header>
  );
};
